import {
  createContext,
  useCallback,
  useEffect,
  useContext,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
// import { toast } from 'react-toastify';
import {
  IPropsCategoriaImovel,
  IPropsCorretor,
  IPropsType,
  IPropsUserLogged,
} from "../types";
// import { toast } from 'react-toastify';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios, { AxiosInstance } from "axios";
import {
  deleteCookie,
  getJsonCookie,
  hasEssentialCookieConsent,
  getCookie,
  setCookie,
  setJsonCookie,
} from "../utils/cookies";

const MySwal = withReactContent(Swal);

const AUTH_COOKIE_KEYS = {
  user: "@HubNogueira:user",
  token: "@HubNogueira:token",
  refreshToken: "@HubNogueira:refreshToken",
} as const;

const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
});

const refreshApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
});

interface User {
  aud: string;
  azp: string;
  email: string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  iat: number;
  iss: string;
  jti: string;
  name: string;
  nbf: number;
  picture: string;
  sub: string;
}

interface AuthState {
  user: IPropsUserLogged;
  token: string;
  refreshToken: string;
}

interface SignInCredentials {
  email: string;
  objUser: User;
}

interface PropsSignInWithEmailAndPassword {
  email: string;
  password: string;
}

interface AuthContextProps {
  user: IPropsUserLogged;
  signIn(credentials: SignInCredentials): Promise<void>;
  signInWithEmailAndPassword(
    credentials: PropsSignInWithEmailAndPassword,
  ): Promise<void>;
  signOut(): void;
  loading: boolean;
  person: IPropsCorretor;
  avatarModification: any;
  setAvatarModification: (val: any) => void;
  categorys: IPropsCategoriaImovel[];
  typeBuild: IPropsType[];
  api: AxiosInstance;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

const AuthProvider = ({ children }: any) => {
  const navigate = useNavigate();
  const authRef = useRef<AuthState | null>(null);

  const [loading, setLoading] = useState(false);
  const [person, setPerson] = useState<IPropsCorretor>({} as IPropsCorretor);
  const [categorys, setCategorys] = useState<IPropsCategoriaImovel[]>([]);
  const [typeBuild, setTypeBuild] = useState<IPropsType[]>([]);
  const [avatarModification, setAvatarModification] = useState<any>();

  const [data, setData] = useState<AuthState>(() => {
    if (!hasEssentialCookieConsent()) {
      return {} as AuthState;
    }

    const token = getCookie(AUTH_COOKIE_KEYS.token);
    const user = getJsonCookie<IPropsUserLogged>(AUTH_COOKIE_KEYS.user);
    const refreshToken = getCookie(AUTH_COOKIE_KEYS.refreshToken);

    if (user && token && refreshToken) {
      const initial = { user, token, refreshToken };
      // Sincroniza antes do 1º paint — efeitos filhos podem disparar requests
      // antes do useEffect que normalmente setaria o authRef.
      authRef.current = initial;
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      return initial;
    }

    return {} as AuthState;
  });

  const persistAuth = useCallback(
    (user: IPropsUserLogged, token: string, refreshToken: string) => {
      const nextAuth = { user, token, refreshToken };

      if (hasEssentialCookieConsent()) {
        setJsonCookie(AUTH_COOKIE_KEYS.user, user, {
          maxAge: AUTH_COOKIE_MAX_AGE,
        });
        setCookie(AUTH_COOKIE_KEYS.token, token, {
          maxAge: AUTH_COOKIE_MAX_AGE,
        });
        setCookie(AUTH_COOKIE_KEYS.refreshToken, refreshToken, {
          maxAge: AUTH_COOKIE_MAX_AGE,
        });
      }

      authRef.current = nextAuth;
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setData(nextAuth);
    },
    [],
  );

  //função de login
  const signIn = useCallback(
    async ({ email, objUser }: SignInCredentials) => {
      setLoading(true);
      try {
        const res = await api.post("/social-login", {
          email,
        });

        const user = {
          email,
          family_name: objUser.family_name,
          given_name: objUser.given_name,
          name: res?.data?.user?.name,
          picture: objUser.picture,
          slug: res?.data?.user?.slug,
          enable_app: res?.data?.user.enable_app,
          id_corretor: res?.data?.user.id_corret_corretor,
          id_pessoa: res?.data?.user.id_pessoa,
        };

        persistAuth(user, res.data.token, res.data.refreshToken);
        navigate("/perfil-interno");
      } catch (e: any) {
        console.log(e);
        MySwal.fire({
          icon: "error",
          title: "Desculpe",
          text: `${e?.response?.data ?? "Não foi possível realizar o login."}`,
          confirmButtonColor: "#0E2244",
        });
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [navigate, persistAuth],
  );

  //função login com email e senha
  const signInWithEmailAndPassword = useCallback(
    async ({ email, password }: PropsSignInWithEmailAndPassword) => {
      setLoading(true);
      try {
        const res = await api.post("/login", { email, password });

        const user = {
          email,
          family_name: res?.data?.user?.apelido,
          given_name: res?.data?.user?.apelido,
          name: res?.data?.user?.apelido?.split("")[0],
          picture: "",
          slug: res?.data?.user?.portal_slug,
          enable_app: res?.data?.user?.enable_app,
          id_corretor: res?.data?.user?.id_corret_corretor,
          id_pessoa: res?.data?.user?.id_pessoa,
        };

        persistAuth(user, res.data.token, res.data.refreshToken);
        navigate("/perfil-interno");
      } catch (e: any) {
        MySwal.fire({
          icon: "error",
          title: "Desculpe",
          text: `${e?.response?.data ?? "Não foi possível realizar o login."}`,
          confirmButtonColor: "#0E2244",
        });
      } finally {
        setLoading(false);
      }
    },
    [navigate, persistAuth],
  );

  //função de carregar informações do corretor logado
  useEffect(() => {
    if (data?.user !== undefined) {
      api
        .get(`/portal/corretor/${data?.user?.id_corretor}`)
        .then((res) => {
          setPerson(res.data);
        })
        .catch((e) => console.log(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  //função para carregar categorias
  useEffect(() => {
    api
      .get("/portal/category")
      .then((res) => {
        setCategorys(res.data);
      })
      .catch((e) => console.log(e))
      .finally();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //função para carregar os tipos de imóveis
  useEffect(() => {
    api
      .get("/portal/tipo?portal=hub")
      .then((res) => {
        setTypeBuild(res.data);
      })
      .catch((e) => console.log(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //função para deslogar
  const signOut = useCallback(() => {
    deleteCookie(AUTH_COOKIE_KEYS.user);
    deleteCookie(AUTH_COOKIE_KEYS.token);
    deleteCookie(AUTH_COOKIE_KEYS.refreshToken);
    delete api.defaults.headers.common["Authorization"];
    authRef.current = null;
    setLoading(false);
    navigate("/");
    setData({} as AuthState);
  }, [navigate]);

  useEffect(() => {
    authRef.current = data;

    if (data?.token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      return;
    }

    delete api.defaults.headers.common["Authorization"];
  }, [data]);

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      async (config) => {
        const token = authRef.current?.token;
        config.headers = config.headers ?? {};

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (
          !config.headers.Authorization &&
          api.defaults.headers.common["Authorization"]
        ) {
          config.headers.Authorization =
            api.defaults.headers.common["Authorization"];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const currentAuth = authRef.current;

        if (
          error?.response?.status === 401 &&
          !originalRequest?._retry &&
          currentAuth?.refreshToken
        ) {
          originalRequest._retry = true;

          try {
            const response = await refreshApi.post(`/refresh-token`, {
              refresh_token: currentAuth.refreshToken,
            });

            if (!response.data.success) {
              signOut();
              return Promise.reject(error);
            }

            const { token, refreshToken } = response.data;

            const user = {
              email: response?.data?.user?.email_social,
              family_name: response?.data?.user?.apelido,
              given_name: response?.data?.user?.apelido,
              name: response?.data?.user?.apelido?.split("")[0],
              picture: "",
              slug: response?.data?.user?.portal_slug,
              enable_app: response?.data?.user?.enable_app,
              id_corretor: response?.data?.user?.id_corret_corretor,
              id_pessoa: response?.data?.user?.id_pessoa,
            };

            persistAuth(user, token, refreshToken);
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          } catch (refreshError) {
            signOut();
            throw refreshError;
          }
        } else if (
          error?.response?.status === 401 &&
          originalRequest?._retry
        ) {
          signOut();
        } else if (
          error?.response?.status === 401 &&
          currentAuth &&
          !currentAuth.refreshToken
        ) {
          signOut();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [persistAuth, signOut]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signInWithEmailAndPassword,
        signOut,
        loading,
        person,
        avatarModification,
        setAvatarModification,
        categorys,
        typeBuild,
        api,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export { AuthProvider, useAuth };
